import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Cpu, Lightbulb, X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { canAny } from '../../api/client';
import { fetchAlarmPage } from '../../api/ops';
import { useDevice, useSiteNames } from '../../data/hooks';
import { deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { useDeviceLive } from '../../realtime/hooks';
import { ConnectivityTag, PresenceBadge, ProtocolTag, SeverityBadge } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { SignalCell } from '../devices/liveCells';
import { useDependants, usePresenceCounts } from '../devices/Topology';
import { formatRelative } from '../../ui/format';
import { useMe } from '../auth/session';
import {
  DeviceControls,
  DeviceMetricsGrid,
  DeviceQueries,
  LightDial,
  MeasuredAt,
  PresenceReason,
} from '../devices/DeviceLive';

function LastSeen({ id }: { id: string }) {
  const lastSeen = useDeviceLive(id, (d) => d?.lastSeenAt);
  return <>{formatRelative(lastSeen)}</>;
}

/**
 * Summary card over the map for one device (UniFi-style side panel; bottom sheet on phones): identity, presence,
 * light output, controls and the latest measurements — all live from the device's own store entry.
 */
export function DeviceCard({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  useStaticVersion((s) => s.version); // re-read static data after registry events
  const s = deviceStatic.get(id);
  const detail = useDevice(id);
  const siteNames = useSiteNames();
  const presence = useDeviceLive(id, (d) => d?.presence);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!s) return null;
  const d = detail.data;
  const capabilities = d?.capabilities ?? s.capabilities;
  const isLight = capabilities.includes('light.set_dimmer');
  const uid = d?.identifiers[0];

  return (
    <aside className="device-card" aria-label={s.name}>
      <header className="device-card-head">
        <span className={`device-card-icon presence-${presence ?? 'unknown'}`}>
          {isLight ? <Lightbulb size={20} strokeWidth={1.6} /> : <Cpu size={20} strokeWidth={1.6} />}
        </span>
        <div className="device-card-title">
          <strong className="ellipsis toolbar">
            {s.name} <ProtocolTag protocol={d?.protocol ?? s.protocol} />
          </strong>
          <span className="muted small ellipsis">
            {s.model ??
              t(`catalog.deviceType.${s.deviceType}` as 'catalog.deviceType.other', {
                defaultValue: s.deviceType,
              })}
            {siteNames.get(s.siteId) ? ` · ${siteNames.get(s.siteId)}` : ''}
          </span>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
          <X size={16} />
        </button>
      </header>

      <div className="device-card-status">
        <span className="toolbar">
          <PresenceBadge presence={presence} />
          <SignalCell id={id} hideEmpty />
          <PresenceReason id={id} />
        </span>
        <span className="muted small">
          {t(s.upstream ? 'presence.lastEvidence' : 'devices.live.lastReport')}: <LastSeen id={id} />
        </span>
        <span className="small">
          <ConnectivityTag device={s} />
        </span>
        <DependantsSummary id={id} />
        <AlarmSummary id={id} />
      </div>

      {isLight && (
        <div className="device-card-hero">
          <LightDial id={id} />
        </div>
      )}

      <DeviceControls id={id} capabilities={capabilities} canControl={d?.canControl ?? false} />
      <DeviceQueries id={id} capabilities={capabilities} canControl={d?.canControl ?? false} />

      <section className="device-card-section">
        <h3>
          {t('devices.detail.telemetry')} <MeasuredAt id={id} />
        </h3>
        <DeviceMetricsGrid id={id} compact />
      </section>

      <footer className="device-card-foot">
        <dl className="kv small">
          {uid && (
            <>
              <dt>{uid.scheme}</dt>
              <dd className="mono">{uid.value}</dd>
            </>
          )}
          {d?.gatewayKey && (
            <>
              <dt>{t('nav.gateways')}</dt>
              <dd className="mono">{d.gatewayKey}</dd>
            </>
          )}
        </dl>
        <Link to={`/devices/${id}`} className="btn">
          {t('devices.live.openDetails')} <ArrowUpRight size={14} />
        </Link>
      </footer>
    </aside>
  );
}

type Fault = { value?: unknown; source?: unknown; threshold?: unknown };

/**
 * Why the marker is coloured: the device's alarm severity (live store, the same field that colours the marker) and,
 * for users who may see alarms, its active alarm types with the value that raised them. The list is fetched for this
 * one device only, again when its severity changes — never by polling or a global refetch.
 */
export function AlarmSummary({ id }: { id: string }) {
  const { t } = useTranslation();
  const severity = useDeviceLive(id, (d) => d?.alarm);
  const canView = canAny(useMe().data, 'alarm.view');
  const q = useQuery({
    queryKey: ['alarms', 'device-active', id, severity],
    queryFn: () => fetchAlarmPage({ device_id: id, state: 'active', limit: 5 }),
    enabled: Boolean(severity) && canView,
    staleTime: 60_000,
  });
  // the authority observer sees no alarms (the server does not send it the severity either)
  if (!severity || !canView) return null;
  const items = q.data?.items ?? [];
  const first = items[0];
  const fault = (first?.details as { faults?: Fault[] } | undefined)?.faults?.[0];
  const value =
    fault && typeof fault.value === 'number' && typeof fault.threshold === 'number'
      ? t('deviceOps.cardAlarmValue', {
          metric: t(`catalog.metric.${String(fault.source)}` as 'catalog.metric.electrical.power_w', {
            defaultValue: String(fault.source ?? ''),
          }),
          value: formatNumber(fault.value, 1),
          threshold: formatNumber(fault.threshold, 1),
        })
      : null;
  return (
    <span
      className="toolbar small"
      style={{ flexBasis: '100%' }}
      role="status"
      aria-label={t('deviceOps.cardAlarm')}
    >
      <SeverityBadge severity={severity} />
      {first && (
        <span>
          {t(`alarmTypes.${first.alarm_type}.name` as 'alarmTypes.device_offline.name', {
            defaultValue: first.type_name_en,
          })}
          {value && <span className="muted"> · {value}</span>}
          {items.length > 1 && (
            <span className="muted"> · {t('deviceOps.cardAlarmMore', { n: items.length - 1 })}</span>
          )}
        </span>
      )}
    </span>
  );
}

/** For a device others are reached through (e.g. a concentrator): live counts of those devices. */
function DependantsSummary({ id }: { id: string }) {
  const { t } = useTranslation();
  const ids = useDependants(id);
  const c = usePresenceCounts(ids);
  if (!ids.length) return null;
  return (
    <Link to={`/devices?conn=${id}`} className="muted small">
      {t('devices.detail.dependants')}:{' '}
      {t('devices.detail.dependantsCount', {
        online: formatNumber(c.online),
        offline: formatNumber(c.offline),
        unknown: formatNumber(c.unknown),
        total: formatNumber(c.total),
      })}
    </Link>
  );
}
